import { useState } from 'react';
import { ImageIcon } from 'lucide-react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import './GenerateImage.css';

const MAX_PROMPT = 2000;

const SIZES = [
  { value: '1k', label: '1080P' },
  { value: '2k', label: '2K' },
  { value: '4k', label: '4K' },
];

const RATIOS = [
  { value: '9:16',  label: '9:16',  desc: '竖屏' },
  { value: '16:9',  label: '16:9',  desc: '横屏' },
  { value: '1:1',   label: '1:1',   desc: '正方形' },
  { value: '4:3',   label: '4:3',   desc: '' },
  { value: '3:4',   label: '3:4',   desc: '' },
  { value: '3:2',   label: '3:2',   desc: '' },
  { value: '2:3',   label: '2:3',   desc: '' },
  { value: '5:4',   label: '5:4',   desc: '' },
  { value: '4:5',   label: '4:5',   desc: '' },
  { value: '21:9',  label: '21:9',  desc: '超宽' },
];

const GenerateImage = () => {
  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '生成图片', path: null },
  ];

  const [title, setTitle]             = useState('');
  const [prompt, setPrompt]           = useState('');
  const [size, setSize]               = useState('2k');
  const [ratio, setRatio]             = useState('9:16');
  const [imageUrlsText, setImageUrlsText] = useState('');

  const [submitting, setSubmitting]   = useState(false);
  const [taskId, setTaskId]           = useState('');
  const [statusText, setStatusText]   = useState('');
  const [error, setError]             = useState('');

  const isValid = prompt.trim().length > 0;

  const handleGenerate = async () => {
    if (!isValid || submitting) return;

    const imageUrls = imageUrlsText
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);

    const payload = {
      prompt: prompt.trim(),
      ...(title.trim() && { title: title.trim() }),
      ...(size       && { size }),
      ...(ratio      && { aspectRatio: ratio }),
      ...(imageUrls.length && { image_urls: imageUrls }),
    };

    try {
      setSubmitting(true);
      setError('');
      setTaskId('');
      setStatusText('');

      const resp = await fetch('/api/video/base_vid/img', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || (data.code !== undefined && data.code !== 0)) {
        throw new Error(data.msg || data.message || `请求失败 (${resp.status})`);
      }

      if (data.task_id) {
        setTaskId(data.task_id);
        setStatusText('图片生成中，请稍后在「我的图片」查看结果');
      } else {
        setStatusText(data.msg || '任务已提交，请稍后在「我的图片」查看结果');
      }
    } catch (e) {
      setError(e.message || '生成失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const showResult = error || taskId || statusText;

  return (
    <div className="generate-image-page">
      <Breadcrumb items={breadcrumbItems} />
      <h1 className="page-title">生成图片</h1>

      <div className="gi-page">

        {/* ── 图片标题（可选） ── */}
        <div className="gi-section">
          <div className="gi-label-row">
            <p className="gi-section-title">图片标题</p>
            <span className="gi-badge gi-badge--opt">可选</span>
          </div>
          <input
            className="gi-input"
            type="text"
            maxLength={100}
            placeholder="为图片起一个标题，方便之后查找"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="gi-char-count">{title.length}/100</div>
        </div>

        {/* ── 图片描述（必填） ── */}
        <div className="gi-section">
          <div className="gi-label-row">
            <p className="gi-section-title">图片描述</p>
            <span className="gi-badge gi-badge--req">必填</span>
          </div>
          <div className="gi-tip-box">
            详细描述画面内容、风格、色调、光线等，描述越具体，生成效果越好。
            如需换装、风格迁移等，可在下方填写参考图片 URL。
          </div>
          <textarea
            className="gi-textarea"
            rows={5}
            maxLength={MAX_PROMPT}
            placeholder="描述你想要生成的图片内容，例如：让图1的女孩穿上图2的婚纱，女孩的面部特征和表情要与参考图片保持一致"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <div className="gi-char-count">{prompt.length}/{MAX_PROMPT}</div>
        </div>

        {/* ── 输出大小（可选） ── */}
        <div className="gi-section">
          <div className="gi-label-row">
            <p className="gi-section-title">输出大小</p>
            <span className="gi-badge gi-badge--opt">可选</span>
          </div>
          <div className="gi-radio-group">
            {SIZES.map((s) => (
              <label
                key={s.value}
                className={`gi-radio-label${size === s.value ? ' active' : ''}`}
              >
                <input
                  type="radio"
                  name="gi-size"
                  value={s.value}
                  checked={size === s.value}
                  onChange={() => setSize(s.value)}
                />
                <span className="gi-radio-dot" />
                {s.label}
              </label>
            ))}
          </div>
          <p className="gi-hint">选择输出图像的分辨率大小</p>
        </div>

        {/* ── 图片比例（可选） ── */}
        <div className="gi-section">
          <div className="gi-label-row">
            <p className="gi-section-title">图片比例</p>
            <span className="gi-badge gi-badge--opt">可选</span>
          </div>
          <div className="gi-radio-group">
            {RATIOS.map((r) => (
              <label
                key={r.value}
                className={`gi-radio-label${ratio === r.value ? ' active' : ''}`}
              >
                <input
                  type="radio"
                  name="gi-ratio"
                  value={r.value}
                  checked={ratio === r.value}
                  onChange={() => setRatio(r.value)}
                />
                <span className="gi-radio-dot" />
                {r.label}
                {r.desc && <span className="gi-radio-desc">{r.desc}</span>}
              </label>
            ))}
          </div>
          <p className="gi-hint">选择输出图像的宽高比例</p>
        </div>

        {/* ── 参考图片（可选） ── */}
        <div className="gi-section">
          <div className="gi-label-row">
            <p className="gi-section-title">参考图片</p>
            <span className="gi-badge gi-badge--opt">可选</span>
          </div>
          <textarea
            className="gi-textarea"
            rows={4}
            placeholder={`输入参考图片的 URL，每行一个\n例如：\nhttps://example.com/image1.jpg\nhttps://example.com/image2.jpg`}
            value={imageUrlsText}
            onChange={(e) => setImageUrlsText(e.target.value)}
          />
          <p className="gi-hint">提供参考图片的 URL，每行一个。可用于图片风格迁移、换装等场景</p>
        </div>

        {/* ── 生成按钮 ── */}
        <div className="gi-generate">
          <button
            className="gi-generate-btn"
            onClick={handleGenerate}
            disabled={!isValid || submitting}
          >
            {submitting
              ? <><div className="gi-spinner" />生成中，请稍候...</>
              : <><ImageIcon size={16} />生成图片</>
            }
          </button>
        </div>

        {/* ── 结果 / 状态 ── */}
        {showResult && (
          <div className="gi-status">
            {error && <div className="gi-status-error">{error}</div>}
            {taskId && (
              <div className="gi-status-info">任务 ID：{taskId}</div>
            )}
            {statusText && (
              <div className="gi-status-info">{statusText}</div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default GenerateImage;
